/**
 * EventTimeline: a vertical rail of everything that has happened, or is
 * planned, at one monitoring location. Admin events (created, start, stop,
 * finalised), temperature limit breaches and strength targets, in time order,
 * with a "Now" marker before the scheduled and pending events.
 * The events themselves are built by `buildTimelineEvents`.
 */
import React, { useMemo, useState } from 'react';
import { Box, Paper, Typography, IconButton, Collapse } from '@mui/material';
import {
  Check as CheckIcon,
  PriorityHigh as AlertIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { formatDateTimeWithZone } from '../../utils/datetime';
import { maturityMeta, resolveStrengthUnit } from '../../utils/maturityDisplay';
import type { ChartDataResponse } from '../../services/pours';
import { buildTimelineEvents, type TimelineEvent } from './eventTimelineModel';

interface EventTimelineProps {
  chartData: ChartDataResponse;
  /** Ambient / site-hardware view: no concrete or strength events. */
  ambient?: boolean;
  /** Location runs maturity (shows Maturity Start, Grade Strength and the
   *  strength targets; without it the targets are left out). */
  showStrength?: boolean;
  /** Defaults to the chart response's maturity unit (backend rule when the
   *  response has no descriptor). */
  maturityUnit?: string;
  /** Defaults to the chart response's company strength unit. */
  strengthUnit?: string;
  timezoneIana?: string;
  defaultCollapsed?: boolean;
  /** Render just the rail, without the bordered card / own header /
   *  collapse, for when the surrounding panel already provides those. */
  hideHeader?: boolean;
}

const NODE = 16;

const nodeStyle = (status: TimelineEvent['status']) => {
  switch (status) {
    case 'done':
      return { borderColor: 'success.main', bgcolor: 'success.main' };
    case 'alert':
      return { borderColor: 'error.main', bgcolor: 'error.main' };
    case 'scheduled':
      return { borderColor: 'primary.main', bgcolor: 'background.paper', borderStyle: 'dashed' };
    default:
      return { borderColor: 'grey.400', bgcolor: 'background.paper' };
  }
};

export const EventTimeline: React.FC<EventTimelineProps> = ({
  chartData,
  ambient = false,
  showStrength = false,
  maturityUnit,
  strengthUnit,
  timezoneIana,
  defaultCollapsed = false,
  hideHeader = false,
}) => {
  const [open, setOpen] = useState(!defaultCollapsed);
  const mUnit = maturityUnit || maturityMeta(chartData).unit;
  const sUnit = resolveStrengthUnit(strengthUnit, chartData.strength_unit);

  const { past, future } = useMemo(
    () =>
      buildTimelineEvents({
        // Strength targets belong to locations that run maturity: a
        // Temperature Only location (even on a maturity pour) lists none.
        chartData: showStrength ? chartData : { ...chartData, target: [] },
        ambient,
        showStrength,
        maturityUnit: mUnit,
        strengthUnit: sUnit,
        formatTime: (ms) => formatDateTimeWithZone(ms, timezoneIana),
      }),
    [chartData, ambient, showStrength, mUnit, sUnit, timezoneIana],
  );

  if (past.length === 0 && future.length === 0) return null;

  const rows: (TimelineEvent | 'now')[] = future.length > 0 ? [...past, 'now', ...future] : past;

  const renderEvent = (e: TimelineEvent, last: boolean) => (
    <Box key={e.key} sx={{ display: 'flex', gap: 1.25 }}>
      {/* Gutter: node + connector */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box
          sx={{
            width: NODE,
            height: NODE,
            borderRadius: '50%',
            flexShrink: 0,
            border: '2px solid',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...nodeStyle(e.status),
          }}
        >
          {e.status === 'done' && <CheckIcon sx={{ fontSize: 11, color: 'common.white' }} />}
          {e.status === 'alert' && <AlertIcon sx={{ fontSize: 11, color: 'common.white' }} />}
        </Box>
        {!last && <Box sx={{ flexGrow: 1, width: '2px', bgcolor: 'divider', minHeight: 14, my: 0.25 }} />}
      </Box>

      {/* Content */}
      <Box sx={{ pb: last ? 0 : 1.75, minWidth: 0, flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            fontSize="0.8rem"
            lineHeight={1.3}
            color={e.status === 'alert' ? 'error.main' : undefined}
          >
            {e.label}
          </Typography>
          <Typography variant="caption" color="text.disabled" fontSize="0.65rem" sx={{ flexShrink: 0 }}>
            {e.category}
          </Typography>
        </Box>
        {e.time != null && (
          <Typography variant="caption" color="text.secondary" display="block" fontSize="0.72rem">
            {e.status === 'scheduled' ? 'Scheduled for ' : ''}
            {formatDateTimeWithZone(e.time, timezoneIana)}
          </Typography>
        )}
        {e.details.map((d) => (
          <Typography key={d} variant="caption" color="text.secondary" display="block" fontSize="0.72rem">
            {d}
          </Typography>
        ))}
        {e.status === 'pending' && (
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.72rem' }} display="block">
            Pending
          </Typography>
        )}
      </Box>
    </Box>
  );

  const timelineBody = (
    <Box sx={{ mt: hideHeader ? 0 : 1 }}>
      {rows.map((row, i) =>
        row === 'now' ? (
          <Box key="now" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
            <Box sx={{ width: NODE, display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
            </Box>
            <Typography variant="caption" fontWeight={700} color="primary.main" fontSize="0.7rem">
              Now
            </Typography>
            <Box sx={{ flexGrow: 1, height: '1px', bgcolor: 'primary.main', opacity: 0.4 }} />
          </Box>
        ) : (
          renderEvent(row, i === rows.length - 1)
        ),
      )}
    </Box>
  );

  // Embedded mode: the surrounding panel supplies the header/collapse.
  if (hideHeader) return timelineBody;

  return (
    <Paper elevation={1} sx={{ p: 1.5 }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Event Time Line
        </Typography>
        <IconButton size="small" sx={{ p: 0.25 }} aria-label={open ? 'Collapse timeline' : 'Expand timeline'}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={open}>{timelineBody}</Collapse>
    </Paper>
  );
};

export default EventTimeline;
